import { create } from 'zustand'

type PrepDrawerViewState = {
  lastTab: string
  setLastTab: (value: string) => void
  collapsed: Record<string, boolean>
  setCollapsed: (key: string, value: boolean) => void
}

const storageKey = 'prep.drawerViewState.v1'

const readInitialState = (): Partial<PrepDrawerViewState> => {
  if (typeof window === 'undefined') {
    return {}
  }
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) {
      return {}
    }
    const parsed = JSON.parse(raw) as Partial<PrepDrawerViewState> | null
    if (!parsed || typeof parsed !== 'object') {
      return {}
    }
    return {
      lastTab: typeof parsed.lastTab === 'string' ? parsed.lastTab : undefined,
      collapsed: typeof parsed.collapsed === 'object' && parsed.collapsed
        ? (parsed.collapsed as Record<string, boolean>)
        : undefined,
    }
  } catch {
    return {}
  }
}

export const usePrepDrawerStore = create<PrepDrawerViewState>((set, get) => {
  const initial = readInitialState()

  const persist = (partial: Partial<PrepDrawerViewState>) => {
    if (typeof window === 'undefined') {
      return
    }
    const next = { ...get(), ...partial }
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ lastTab: next.lastTab, collapsed: next.collapsed }),
      )
    } catch {
      /* ignore persistence errors */
    }
  }

  return {
    lastTab: initial.lastTab ?? 'read',
    setLastTab: (value) => {
      set({ lastTab: value })
      persist({ lastTab: value })
    },
    collapsed: initial.collapsed ?? {},
    setCollapsed: (key, value) => {
      set((state) => {
        const next = { ...state.collapsed, [key]: value }
        persist({ collapsed: next })
        return { collapsed: next }
      })
    },
  }
})

